import { useEffect, useState } from 'react'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { paymentReads } from '../../api/fee'
import type { Invoice, InvoiceReportSettings } from '../../api/types'
import { ConfirmButton, Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatDateTime, formatNumberN } from '../../lib/format'
import { useMoneyAction } from '../../lib/useMoneyAction'
import { defaultReportMonth, monthParam } from '../../lib/month'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Port of `AdminInvoiceReportComponent` (`projects/:projectId/admin/invoices`):
 * the month's Stripe invoices (`GET …/admin/payment/invoices/all?month=YYYYMM`),
 * the monthly report settings (`GET|PATCH …/admin/payment/invoices/report`)
 * and "send now" (`POST …/report/send?month=`, legacy `confirm("Are you sure?")`
 * — now an inline confirmation).
 */
export function AdminInvoiceReportPage() {
  const { projectId, project } = useProject()
  const def = defaultReportMonth()
  const [year, setYear] = useState(def.year)
  const [month, setMonth] = useState(def.month)
  const [searched, setSearched] = useState(monthParam(def.year, def.month))
  const invoices = useApiQuery(() => paymentReads.allInvoices(projectId, searched), [projectId, searched])
  const settingsQ = useApiQuery(() => paymentReads.invoiceReport(projectId), [projectId])
  const [settings, setSettings] = useState<InvoiceReportSettings | null>(null)
  const actions = useMoneyAction()

  useEffect(() => {
    if (settingsQ.data) {
      setSettings({
        ...settingsQ.data,
        enabled: settingsQ.data.enabled ? 1 : 0,
        emails: Array.isArray(settingsQ.data.emails) ? settingsQ.data.emails : [],
      })
    }
  }, [settingsQ.data])

  const years: number[] = []
  for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y)

  const columns: Column<Invoice>[] = [
    { key: 'number', label: 'Number' },
    { key: 'createdAt', label: 'Created at', format: (v) => formatDateTime(v as string) },
    { key: 'total', label: 'Total', align: 'right', numeric: true, format: (v) => formatNumberN(v as number, 2) },
    { key: 'currency', label: 'Currency', format: (v) => String(v ?? '').toUpperCase() },
    { key: 'status', label: 'Status' },
    { key: 'customerEmail', label: 'Email' },
    { key: 'customerName', label: 'Name' },
    {
      key: 'address',
      label: 'Address',
      sortValue: (r) => r.customerCountry ?? '',
      format: (_v, r) =>
        [r.customerCountry, r.customerPostalCode, r.customerState, r.customerCity, r.customerLine1, r.customerLine2]
          .filter(Boolean)
          .join(', '),
    },
    {
      key: 'lines',
      label: 'Items',
      sortable: false,
      format: (_v, r) => (
        <ul className="fs-plain-list">
          {(r.lines ?? []).map((l, i) => (
            <li key={i}>
              {l.description}
              {l.quantity > 1 ? ` x ${l.quantity}` : ''}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'invoicePdf',
      label: '',
      sortable: false,
      format: (v) =>
        v ? (
          <a href={String(v)} target="_blank" rel="noopener noreferrer">
            Download PDF from Stripe
          </a>
        ) : null,
    },
  ]

  const save = async () => {
    if (!settings) return
    const body: InvoiceReportSettings = {
      ...settings,
      emails: Array.from(new Set(settings.emails.map((e) => e.trim()).filter(Boolean))),
    }
    const result = await actions.run('save', JSON.stringify(body), () => paymentReads.modifyInvoiceReport(projectId, body))
    if (result) {
      actions.setFeedback('save', { tone: 'success', text: 'Settings saved.' })
      settingsQ.refetch()
    }
  }

  const send = async () => {
    const result = await actions.run('send', searched, () => paymentReads.sendInvoiceReport(projectId, searched))
    if (result !== undefined) actions.setFeedback('send', { tone: 'success', text: 'Report sent' })
  }

  return (
    <div className="portal-card">
      <PageTitle title={`Invoices — ${project.name}`} />
      <div className="portal-grid-2">
        <div>
          <form
            className="portal-toolbar"
            onSubmit={(e) => {
              e.preventDefault()
              setSearched(monthParam(year, month))
            }}
          >
            <label>
              Year{' '}
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Month{' '}
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit">Search</button>
          </form>
          {settings && settings.emails.length > 0 && (
            <ConfirmButton
              label={`Send report (${searched.slice(0, 4)}. ${searched.slice(4)}.) NOW`}
              pendingText="Are you sure?"
              busy={actions.isBusy('send')}
              onConfirm={() => void send()}
            />
          )}
          <Notice feedback={actions.feedbackFor('send')} />
        </div>
        {settings && (
          <form
            className="portal-form"
            aria-label="Invoice report settings"
            onSubmit={(e) => {
              e.preventDefault()
              void save()
            }}
          >
            <label>
              Send report on the 2. day of every month?
              <select
                value={settings.enabled ? 1 : 0}
                onChange={(e) => setSettings({ ...settings, enabled: Number(e.target.value) ? 1 : 0 })}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </label>
            {settings.enabled ? (
              <>
                <label>
                  Email template
                  <input
                    value={settings.emailTemplate ?? ''}
                    onChange={(e) => setSettings({ ...settings, emailTemplate: e.target.value })}
                  />
                </label>
                <fieldset className="fs-fieldset">
                  <legend>Email addresses</legend>
                  {settings.emails.map((addr, i) => (
                    <div key={i} className="portal-input-group">
                      <input
                        type="email"
                        aria-label={`Email address ${i + 1}`}
                        value={addr}
                        onChange={(e) => {
                          const emails = [...settings.emails]
                          emails[i] = e.target.value
                          setSettings({ ...settings, emails })
                        }}
                      />
                      <button
                        type="button"
                        aria-label={`Remove email address ${i + 1}`}
                        onClick={() => setSettings({ ...settings, emails: settings.emails.filter((_, j) => j !== i) })}
                      >
                        −
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setSettings({ ...settings, emails: [...settings.emails, ''] })}>
                    + Add email address
                  </button>
                </fieldset>
              </>
            ) : null}
            <button type="submit" disabled={actions.isBusy('save')}>
              {actions.isBusy('save') ? 'Saving…' : 'Save'}
            </button>
            <Notice feedback={actions.feedbackFor('save')} />
          </form>
        )}
      </div>
      <QueryState loading={invoices.loading} error={invoices.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={invoices.data ?? []} rowKey={(r) => r.id} initialSort={{ key: 'createdAt', dir: 'desc' }} />
        </div>
        {invoices.data?.length === 0 && <p className="portal-text-muted">No invoices in this month.</p>}
      </QueryState>
    </div>
  )
}
